import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Minus, AlertCircle } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Input } from "../ui/Input";
import { UserSelector } from "./UserSelector";
import { PricePointSelector } from "./PricePointSelector";
import { RegistrationFooter } from "./RegistrationFooter";
import { raceRegistrationSchema, type RaceRegistrationFormData } from "../../lib/schemas/race-registration";
import type { Event } from "../../lib/types";
import type { AccountUser } from "../../lib/hooks/useAccountUsers";
import { useEffect } from "react";
import { TEXT_CONTENT } from "../../lib/text-content";

interface RaceRegistrationFormProps {
  event: Event;
  allAccountUsers: AccountUser[];
  availablePricePoints: NonNullable<Event["pricePoints"]>;
  bookingOpen: boolean;
  onSubmit: (data: RaceRegistrationFormData, stayOnPage: boolean) => Promise<void>;
  isSubmitting: boolean;
  currency: string;
}

export function RaceRegistrationForm({
  event,
  allAccountUsers,
  availablePricePoints,
  bookingOpen,
  onSubmit,
  isSubmitting,
  currency,
}: RaceRegistrationFormProps) {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RaceRegistrationFormData>({
    resolver: zodResolver(raceRegistrationSchema),
    defaultValues: {
      eventPricePointId: availablePricePoints.length === 1 ? availablePricePoints[0].id : "",
      boatClass: "",
      sailNumber: "",
      crew: [{ firstName: "", lastName: "", userId: "other" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "crew",
  });

  const crew = watch("crew");
  const eventPricePointId = watch("eventPricePointId");

  useEffect(() => {
    if (availablePricePoints.length === 1 && !eventPricePointId) {
      setValue("eventPricePointId", availablePricePoints[0].id);
    }
  }, [availablePricePoints, eventPricePointId, setValue]);

  const selectedPricePoint = availablePricePoints.find((pp) => pp.id === eventPricePointId);
  const boatPriceCents = selectedPricePoint?.priceCents ?? 0;

  const onUserSelect = (index: number, userId: string) => {
    setValue(`crew.${index}.userId`, userId);
    if (userId !== "other") {
      const selectedUser = allAccountUsers.find((u) => u.id === userId);
      if (selectedUser) {
        setValue(`crew.${index}.firstName`, selectedUser.firstName);
        setValue(`crew.${index}.lastName`, selectedUser.lastName);
      }
    } else {
      setValue(`crew.${index}.firstName`, "");
      setValue(`crew.${index}.lastName`, "");
    }
  };

  const selectedUserIds: (string | "other")[] = crew.map((c) =>
    c.userId && c.userId !== "other" ? c.userId : "other",
  );

  if (event.isCancelled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Enter your boat</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <div>
              <div className="font-medium">{TEXT_CONTENT.EVENTS.CANCELLED_TITLE}</div>
              <div>{TEXT_CONTENT.EVENTS.CANCELLED_DESC}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Enter your boat</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Boat class"
            placeholder="e.g. Laser, 420"
            {...control.register("boatClass")}
            error={errors.boatClass?.message}
            disabled={!bookingOpen}
          />
          <Input
            label="Sail number"
            placeholder="e.g. 12345"
            {...control.register("sailNumber")}
            error={errors.sailNumber?.message}
            disabled={!bookingOpen}
          />
        </div>

        <div>
          <PricePointSelector
            value={eventPricePointId}
            categoryPricePoints={availablePricePoints}
            onChange={(value) => setValue("eventPricePointId", value)}
            disabled={!bookingOpen}
            required
            error={errors.eventPricePointId?.message}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Crew</label>
          <div className="space-y-3">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-100"
              >
                <UserSelector
                  value={crew[index]?.userId ?? "other"}
                  availableUsers={allAccountUsers.filter((u) => {
                    const alreadySelected = selectedUserIds.some(
                      (id, i) => i !== index && id === u.id,
                    );
                    return !alreadySelected;
                  })}
                  allAccountUsers={allAccountUsers}
                  selectedUserIds={selectedUserIds}
                  index={index}
                  onChange={(value) => onUserSelect(index, value)}
                  disabled={!bookingOpen}
                />
                {(crew[index]?.userId === "other" || !crew[index]?.userId) && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      label={fields.length === 1 ? "First name" : `Crew ${index + 1} first name`}
                      placeholder="First name"
                      value={crew[index]?.firstName ?? ""}
                      onChange={(e) => setValue(`crew.${index}.firstName`, e.target.value)}
                      disabled={!bookingOpen}
                      error={errors.crew?.[index]?.firstName?.message}
                    />
                    <Input
                      label={fields.length === 1 ? "Last name" : `Crew ${index + 1} last name`}
                      placeholder="Last name"
                      value={crew[index]?.lastName ?? ""}
                      onChange={(e) => setValue(`crew.${index}.lastName`, e.target.value)}
                      disabled={!bookingOpen}
                      error={errors.crew?.[index]?.lastName?.message}
                    />
                  </div>
                )}
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => remove(index)}
                    disabled={!bookingOpen || fields.length <= 1}
                  >
                    <Minus className="w-4 h-4 mr-1" />
                    Remove crew member
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ firstName: "", lastName: "", userId: "other" })}
              disabled={!bookingOpen}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add crew member
            </Button>
          </div>
          {errors.crew?.root?.message && (
            <p className="text-sm text-red-600 mt-1">{errors.crew.root.message}</p>
          )}
        </div>

        <RegistrationFooter
          totalCents={boatPriceCents}
          currency={currency}
          totalLabel="Total (per boat)"
          bookingOpen={bookingOpen}
          availablePricePointsLength={availablePricePoints.length}
          bookingClosesAt={event.bookingClosesAt}
          isSubmitting={isSubmitting}
          onAddAndContinue={handleSubmit((data) => onSubmit(data, true))}
          onAddAndCheckout={handleSubmit((data) => onSubmit(data, false))}
        />
      </CardContent>
    </Card>
  );
}
